import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import { toast } from 'react-toastify';

const TicketSubstatusConfig = () => {
  const { theme } = useContext(ThemeContext);
  const [statuses, setStatuses] = useState([]);
  const [substatuses, setSubstatuses] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    is_internal: true,
    is_public: true,
    status_ids: [],
  });

  const loadStatuses = async () => {
    try {
      const { data } = await http.get('/ticket-statuses');
      setStatuses(data || []);
    } catch (e) {
      toast.error('Erro ao carregar status.');
    }
  };

  const loadSubstatuses = async () => {
    try {
      const { data } = await http.get('/ticket-substatuses');
      setSubstatuses(data || []);
    } catch (e) {
      toast.error('Erro ao carregar justificativas.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStatuses();
      await loadSubstatuses();
      setLoading(false);
    })();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', is_internal: true, is_public: true, status_ids: [] });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      is_internal: s.is_internal !== false,
      is_public: s.is_public !== false,
      status_ids: (s.statuses || []).map((st) => st.id),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.warn('Informe o nome da justificativa.');
      return;
    }
    try {
      if (editingId) {
        await http.put(`/ticket-substatuses/${editingId}`, {
          name: form.name.trim(),
          is_internal: form.is_internal,
          is_public: form.is_public,
          status_ids: form.status_ids,
        });
        toast.success('Justificativa atualizada.');
      } else {
        await http.post('/ticket-substatuses', {
          name: form.name.trim(),
          is_internal: form.is_internal,
          is_public: form.is_public,
          status_ids: form.status_ids,
        });
        toast.success('Justificativa criada.');
      }
      setModalOpen(false);
      await loadSubstatuses();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao salvar.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta justificativa?')) return;
    try {
      await http.delete(`/ticket-substatuses/${id}`);
      toast.success('Justificativa excluída.');
      await loadSubstatuses();
    } catch (e) {
      toast.error('Erro ao excluir.');
    }
  };

  // Busca sem diferenciar maiuscula, e sem diferenciar acento: "acao" acha
  // "Ação". Sem isso a busca falharia justamente nos nomes em portugues, que
  // sao a maioria aqui -- e o operador nao tem como saber que precisa digitar
  // o acento certo.
  const semAcento = (texto) => (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const termo = semAcento(busca).trim();
  const substatusesFiltradas = termo
    ? substatuses.filter((s) => semAcento(s.name).includes(termo))
    : substatuses;

  if (loading) {
    return <Typography color="text.secondary">Carregando...</Typography>;
  }

  return (
    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
          Justificativas (Substatus)
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Justificativas são complementos pré-definidos para um status. Vincule cada justificativa a um ou mais status e defina se vale para tickets internos e/ou públicos.
        </Typography>
        {/*
          A busca existe porque esta tela renderiza TODAS as justificativas
          de uma vez, sem paginacao -- Gisele: "Filtro para criacao de
          justificativas". Quando sao dezenas, achar a que se quer editar
          vira caca visual.

          Mesmo padrao da busca de FiltersPage: filtra no navegador, sobre a
          lista que ja esta em memoria. Nao vira parametro de API de
          proposito -- a lista e pequena e ja veio inteira, entao ida ao
          servidor so acrescentaria espera.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
            Nova justificativa
          </Button>
          <TextField
            placeholder="Buscar por nome..."
            size="small"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            inputProps={{ 'aria-label': 'Buscar justificativa por nome' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {substatusesFiltradas.map((s) => (
            <Card key={s.id} variant="outlined" sx={{ minWidth: 220, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {s.name}
                  </Typography>
                  <Box>
                    <Button size="small" onClick={() => openEdit(s)} sx={{ textTransform: 'none', mr: 0.5 }}>
                      Editar
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(s.id)} sx={{ textTransform: 'none' }}>
                      Excluir
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" display="block">
                  {s.is_internal && 'Interno '}
                  {s.is_public && 'Público'}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {(s.statuses || []).map((st) => (
                    <Chip key={st.id} size="small" label={st.name} sx={{ mr: 0.5, mt: 0.5 }} />
                  ))}
                  {(s.statuses || []).length === 0 && (
                    <Typography variant="caption" color="text.secondary">Nenhum status vinculado</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Editar justificativa' : 'Nova justificativa'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ mt: 1, mb: 2 }}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Status relacionados (multiseleção)</Typography>
            <Select
              fullWidth
              multiple
              value={form.status_ids}
              onChange={(e) => setForm({ ...form, status_ids: e.target.value })}
              renderValue={(selected) => {
                const names = (selected || []).map((id) => statuses.find((s) => s.id === id)?.name).filter(Boolean);
                return names.join(', ') || 'Nenhum';
              }}
              sx={{ mb: 2 }}
            >
              {statuses.map((st) => (
                <MenuItem key={st.id} value={st.id}>
                  <Checkbox checked={form.status_ids.indexOf(st.id) > -1} />
                  <ListItemText primary={st.name} secondary={st.stage?.name} />
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={<Switch checked={form.is_internal} onChange={(e) => setForm({ ...form, is_internal: e.target.checked })} />}
              label="Para tickets internos"
            />
            <br />
            <FormControlLabel
              control={<Switch checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />}
              label="Para tickets públicos"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}>Salvar</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TicketSubstatusConfig;
